/**
 * VirtualTourViewer — Matterport-style navigable virtual tour built from a
 * saved Tour entity (design_tours) + its ordered panorami_360 scenes.
 *
 * Uses @photo-sphere-viewer/virtual-tour-plugin (official plugin) for:
 *   - Smooth cross-fade transitions between scenes
 *   - Directional AR-style arrows in the scene (Matterport-like)
 *   - Auto-generated grid mini-map (drag-anywhere, click-to-jump)
 *   - Compass overlay + Gallery thumbnail rail + Keyboard nav
 *
 * Props:
 *   - tour          : the design_tours document ({nome, panorama_ids, viewpoints[]})
 *   - panoramiFull  : array of panorami_360 docs already fetched with `image_base64` field
 *   - onClose()
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Viewer } from "@photo-sphere-viewer/core";
import { VirtualTourPlugin } from "@photo-sphere-viewer/virtual-tour-plugin";
import { MarkersPlugin } from "@photo-sphere-viewer/markers-plugin";
import { GalleryPlugin } from "@photo-sphere-viewer/gallery-plugin";
import { CompassPlugin } from "@photo-sphere-viewer/compass-plugin";

import "@photo-sphere-viewer/core/index.css";
import "@photo-sphere-viewer/markers-plugin/index.css";
import "@photo-sphere-viewer/virtual-tour-plugin/index.css";
import "@photo-sphere-viewer/gallery-plugin/index.css";
import "@photo-sphere-viewer/compass-plugin/index.css";

import { Button } from "./components/ui/button";
import { X, Info, Play, Pause } from "lucide-react";

const vtourStyles = (
  <style>{`
    .vtour-poi {
      color: #fff; font-weight: 600; font-size: 11px; line-height: 1;
      padding: 6px 12px; border-radius: 999px; border: 2px solid #fff;
      box-shadow: 0 4px 14px rgba(0,0,0,.45); cursor: pointer;
      display: inline-flex; align-items: center; gap: 5px;
      transition: transform .15s, filter .15s, box-shadow .15s;
      max-width: 200px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
    }
    .vtour-poi::before {
      content: ""; width: 6px; height: 6px; border-radius: 50%; background: #fff; box-shadow: 0 0 0 2px rgba(255,255,255,.5);
      flex-shrink: 0;
    }
    .vtour-poi:hover { transform: scale(1.10); filter: brightness(1.15); box-shadow: 0 6px 20px rgba(0,0,0,.55); }
    @keyframes vtourProgress { from { width: 0%; } to { width: 100%; } }
  `}</style>
);

/**
 * Build a small grid floor-plan PNG data URL with N labeled dots.
 * Returns { imageUrl, coordsById } where coordsById maps nodeId → [x,y] in image pixels.
 */
const buildGridFloorPlan = (nodes) => {
  const n = nodes.length;
  if (!n) return { imageUrl: "", coordsById: {} };
  const cols = Math.ceil(Math.sqrt(n));
  const rows = Math.ceil(n / cols);
  const cell = 120;
  const pad = 30;
  const W = cols * cell + pad * 2;
  const H = rows * cell + pad * 2;
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");
  // Background (light grid)
  const grad = ctx.createLinearGradient(0, 0, W, H);
  grad.addColorStop(0, "#eef2ff");
  grad.addColorStop(1, "#f5f3ff");
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, W, H);
  ctx.strokeStyle = "rgba(99,102,241,0.16)";
  ctx.lineWidth = 1;
  for (let x = pad; x <= W - pad; x += 20) { ctx.beginPath(); ctx.moveTo(x, pad); ctx.lineTo(x, H - pad); ctx.stroke(); }
  for (let y = pad; y <= H - pad; y += 20) { ctx.beginPath(); ctx.moveTo(pad, y); ctx.lineTo(W - pad, y); ctx.stroke(); }

  // Border
  ctx.strokeStyle = "#a5b4fc";
  ctx.lineWidth = 2;
  ctx.strokeRect(pad - 4, pad - 4, W - (pad - 4) * 2, H - (pad - 4) * 2);

  // Path lines linear connect
  ctx.strokeStyle = "rgba(79,70,229,0.55)";
  ctx.lineWidth = 3;
  ctx.beginPath();
  const centers = nodes.map((_, i) => {
    const r = Math.floor(i / cols), c = i % cols;
    return [pad + c * cell + cell / 2, pad + r * cell + cell / 2];
  });
  centers.forEach(([x, y], i) => {
    if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
  });
  ctx.stroke();

  // Node dots + labels
  const coordsById = {};
  nodes.forEach((node, i) => {
    const [x, y] = centers[i];
    coordsById[node.id] = [x, y];
    // Outer ring
    ctx.beginPath();
    ctx.arc(x, y, 18, 0, Math.PI * 2);
    ctx.fillStyle = "#4f46e5";
    ctx.fill();
    // Inner white
    ctx.beginPath();
    ctx.arc(x, y, 14, 0, Math.PI * 2);
    ctx.fillStyle = "#fff";
    ctx.fill();
    // Number
    ctx.fillStyle = "#4f46e5";
    ctx.font = "bold 14px system-ui, sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(String(i + 1), x, y);
    // Label
    ctx.fillStyle = "#1e1b4b";
    ctx.font = "600 10px system-ui, sans-serif";
    ctx.textBaseline = "top";
    const lbl = (node.name || `#${i + 1}`).slice(0, 14);
    ctx.fillText(lbl, x, y + 22);
  });

  return { imageUrl: canvas.toDataURL("image/png"), coordsById, width: W, height: H };
};

export default function VirtualTourViewer({ tour, panoramiFull, onClose }) {
  const containerRef = useRef(null);
  const viewerRef = useRef(null);
  const [currentNodeId, setCurrentNodeId] = useState(null);
  const [error, setError] = useState("");
  // Auto-Walk mode inside VR Tour (Feb 2026)
  const [autoWalkOn, setAutoWalkOn] = useState(false);
  const [autoWalkDwell, setAutoWalkDwell] = useState(4000);
  const [autoWalkPoiIndex, setAutoWalkPoiIndex] = useState(0);
  const [autoWalkLabel, setAutoWalkLabel] = useState("");
  const [autoWalkDetail, setAutoWalkDetail] = useState("");

  // Build node list from the tour ordering
  const nodes = useMemo(() => {
    if (!tour || !panoramiFull) return [];
    const byId = new Map(panoramiFull.map(p => [p.id, p]));
    return (tour.panorama_ids || []).map((pid, i) => {
      const p = byId.get(pid);
      if (!p) return null;
      const vp = (tour.viewpoints || [])[i] || { yaw: 0, pitch: 0 };
      return {
        id: pid,
        panorama: p.image_base64,
        thumbnail: p.thumbnail || p.image_base64,
        name: p.nome || `Scena ${i + 1}`,
        caption: p.nome || `Scena ${i + 1}`,
        sphereCorrection: {},
        internalViewpoints: p.internal_viewpoints || [],
        // Initial camera direction when entering this node
        panoData: undefined,
        // Custom metadata used by our arrow builder
        _initialYaw: Number(vp.yaw) || 0,
        _initialPitch: Number(vp.pitch) || 0,
      };
    }).filter(Boolean);
  }, [tour, panoramiFull]);

  // Build links: linear (i → i+1 and i+1 → i)
  const nodesWithLinks = useMemo(() => {
    return nodes.map((n, i) => {
      const links = [];
      if (i > 0) {
        // Back link
        links.push({
          nodeId: nodes[i - 1].id,
          // Arrow appears at yaw+180° from initial (roughly behind you). We approximate with π.
          position: { yaw: Math.PI, pitch: -0.1 },
        });
      }
      if (i < nodes.length - 1) {
        // Forward link
        links.push({
          nodeId: nodes[i + 1].id,
          position: { yaw: 0, pitch: -0.1 },
        });
      }
      return { ...n, links };
    });
  }, [nodes]);

  // Floor plan (grid)
  const floorPlan = useMemo(() => buildGridFloorPlan(nodes), [nodes]);

  useEffect(() => {
    if (!containerRef.current || nodesWithLinks.length === 0) return;
    if (viewerRef.current) { try { viewerRef.current.destroy(); } catch (e) { /* noop */ } }

    let viewer;
    try {
      viewer = new Viewer({
        container: containerRef.current,
        loadingImg: "",
        touchmoveTwoFingers: false,
        mousewheelCtrlKey: false,
        defaultZoomLvl: 0,
        maxFov: 110,
        minFov: 30,
        navbar: ["zoom", "move", "gallery", "caption", "fullscreen"],
        plugins: [
          MarkersPlugin,
          [GalleryPlugin, {
            thumbnailSize: { width: 100, height: 100 },
            visibleOnLoad: true,
          }],
          [CompassPlugin, { size: "80px", position: "top right" }],
          [VirtualTourPlugin, {
            positionMode: "manual",
            renderMode: "3d", // Matterport-style 3D arrows in the scene
            startNodeId: nodesWithLinks[0].id,
            transitionOptions: {
              showLoader: true,
              speed: "10rpm",
              fadeIn: true,
              rotation: true,
            },
            arrowStyle: {
              color: "#4f46e5",
              hoverColor: "#a855f7",
              outlineColor: "#ffffff",
              scale: [0.5, 2],
            },
            nodes: nodesWithLinks.map(n => ({
              id: n.id,
              panorama: n.panorama,
              thumbnail: n.thumbnail,
              name: n.name,
              caption: n.caption,
              links: n.links,
            })),
          }],
        ],
      });
      viewerRef.current = viewer;

      // Track current node for UI
      const vt = viewer.getPlugin(VirtualTourPlugin);
      const mp = viewer.getPlugin(MarkersPlugin);

      /** Render internal viewpoint markers (POI) for the current scene. */
      const applyPoiMarkers = (nodeId) => {
        if (!mp) return;
        const node = nodesWithLinks.find(n => n.id === nodeId);
        const vps = node?.internalViewpoints || [];
        try {
          mp.setMarkers(vps.map(v => ({
            id: `poi-${v.id}`,
            position: { yaw: Number(v.yaw) || 0, pitch: Number(v.pitch) || 0 },
            html: `<div class="vtour-poi" style="background:${v.color || '#4f46e5'};" title="${(v.label || 'Vista').replace(/"/g,'&quot;')}"><span>${(v.label || '•').slice(0,20)}</span></div>`,
            anchor: "center center",
            data: {
              kind: "poi",
              yaw: Number(v.yaw) || 0,
              pitch: Number(v.pitch) || 0,
              zoom: Number(v.zoom || 50),
              step_x: Number(v.step_x || 0),
              step_z: Number(v.step_z || 0),
              label: v.label,
            },
          })));
        } catch (_) { /* noop */ }
      };

      if (mp && mp.addEventListener) {
        mp.addEventListener("select-marker", ({ marker }) => {
          if (marker.data?.kind === "poi") {
            try {
              const stepX = Number(marker.data.step_x || 0);
              const stepZ = Number(marker.data.step_z || 0);
              const targetYaw = Number(marker.data.yaw) + (stepX * Math.PI / 8);
              const targetPitch = Number(marker.data.pitch) - (stepZ * Math.PI / 16);
              const targetZoom = Math.max(0, Math.min(100, Number(marker.data.zoom || 50) + (stepZ * 20)));
              viewer.animate({ yaw: targetYaw, pitch: targetPitch, zoom: targetZoom, speed: "5rpm" });
            } catch (_) { /* noop */ }
          }
        });
      }

      // Apply initially + on every scene change
      applyPoiMarkers(nodesWithLinks[0].id);

      if (vt && vt.addEventListener) {
        vt.addEventListener("node-changed", (e) => {
          const nid = e?.node?.id || e?.nodeId;
          if (nid) {
            setCurrentNodeId(nid);
            // Slight delay so viewer swap completes before adding markers
            setTimeout(() => applyPoiMarkers(nid), 200);
          }
        });
      }

      // Keyboard arrows to nav between nodes
      const onKey = (e) => {
        if (!nodesWithLinks.length) return;
        const activeId = currentNodeId || nodesWithLinks[0].id;
        const idx = nodesWithLinks.findIndex(x => x.id === activeId);
        let nextId = null;
        if ((e.key === "ArrowRight" || e.key === "ArrowDown") && idx < nodesWithLinks.length - 1) {
          nextId = nodesWithLinks[idx + 1].id;
        } else if ((e.key === "ArrowLeft" || e.key === "ArrowUp") && idx > 0) {
          nextId = nodesWithLinks[idx - 1].id;
        }
        if (nextId && vt && vt.setCurrentNode) {
          vt.setCurrentNode(nextId);
        }
      };
      window.addEventListener("keydown", onKey);
      viewer._customCleanup = () => window.removeEventListener("keydown", onKey);
    } catch (e) {
      setError(String(e?.message || e));
    }

    return () => {
      try { viewer?._customCleanup?.(); viewer?.destroy?.(); } catch (_) { /* noop */ }
      viewerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodesWithLinks]);

  // Rotate viewer to a POI (with walking illusion) — declared here to allow effect below
  const animateToPoi = (poi) => {
    if (!viewerRef.current || !poi) return;
    try {
      const stepX = Number(poi.step_x || 0);
      const stepZ = Number(poi.step_z || 0);
      const targetYaw = Number(poi.yaw) + (stepX * Math.PI / 8);
      const targetPitch = Number(poi.pitch) - (stepZ * Math.PI / 16);
      const targetZoom = Math.max(0, Math.min(100, Number(poi.zoom || 50) + (stepZ * 20)));
      viewerRef.current.animate({ yaw: targetYaw, pitch: targetPitch, zoom: targetZoom, speed: "5rpm" });
    } catch (_) { /* noop */ }
  };

  // Auto-Walk Presentation driver (must be called unconditionally, before any early return)
  useEffect(() => {
    if (!autoWalkOn || !nodesWithLinks.length) { setAutoWalkLabel(""); setAutoWalkDetail(""); return; }
    const node = nodesWithLinks.find(n => n.id === (currentNodeId || nodesWithLinks[0].id));
    if (!node) return;
    const pois = node.internalViewpoints || [];

    if (pois.length > 0 && autoWalkPoiIndex < pois.length) {
      const currentPoi = pois[autoWalkPoiIndex];
      setAutoWalkLabel(currentPoi.label || `POI ${autoWalkPoiIndex + 1}`);
      setAutoWalkDetail(`Scena "${node.name}" · POI ${autoWalkPoiIndex + 1}/${pois.length}`);
      animateToPoi(currentPoi);
      const t = setTimeout(() => setAutoWalkPoiIndex(i => i + 1), autoWalkDwell);
      return () => clearTimeout(t);
    }

    // POIs exhausted → advance to next scene
    const idx = nodesWithLinks.findIndex(n => n.id === node.id);
    const nextIdx = (idx + 1) % nodesWithLinks.length;
    if (nextIdx === idx) { setAutoWalkOn(false); return; }
    setAutoWalkLabel("Cambio scena…");
    setAutoWalkDetail(`Vado a "${nodesWithLinks[nextIdx].name}"`);
    const t = setTimeout(() => {
      const vt = viewerRef.current?.getPlugin?.(VirtualTourPlugin);
      if (vt && vt.setCurrentNode) {
        try { vt.setCurrentNode(nodesWithLinks[nextIdx].id); } catch (_) { /* noop */ }
      }
      setAutoWalkPoiIndex(0);
    }, Math.min(2500, autoWalkDwell));
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoWalkOn, autoWalkPoiIndex, currentNodeId, autoWalkDwell, nodesWithLinks]);

  if (!tour || nodesWithLinks.length < 2) {
    return (
      <div className="p-6 text-center text-gray-600 bg-gray-50 rounded-lg">
        <p>Servono almeno 2 panorami collegati in questo tour per la modalità navigabile.</p>
        <Button onClick={onClose} variant="outline" className="mt-3">Chiudi</Button>
      </div>
    );
  }

  const currentIdx = nodesWithLinks.findIndex(n => n.id === (currentNodeId || nodesWithLinks[0].id));
  const currentNode = nodesWithLinks[Math.max(0, currentIdx)];

  // Handle click on custom mini-map node
  const jumpTo = (nodeId) => {
    const vt = viewerRef.current?.getPlugin?.(VirtualTourPlugin);
    if (vt && vt.setCurrentNode) {
      try { vt.setCurrentNode(nodeId); } catch (_) { /* noop */ }
    }
  };

  const startAutoWalk = () => { setAutoWalkPoiIndex(0); setAutoWalkOn(true); };
  const stopAutoWalk = () => { setAutoWalkOn(false); setAutoWalkPoiIndex(0); };


  return (
    <div className="fixed inset-0 z-[100] bg-black" data-testid="vtour-viewer">
      {vtourStyles}
      {/* Header bar */}
      <div className="absolute top-0 left-0 right-0 z-10 bg-gradient-to-b from-black/80 to-transparent p-3 flex items-center justify-between text-white pointer-events-none">
        <div className="pointer-events-auto flex items-center gap-3">
          <div className="font-semibold text-sm truncate max-w-[50vw]" title={tour.nome}>{tour.nome}</div>
          <div className="text-xs text-white/80 hidden sm:block">
            {currentIdx + 1}/{nodesWithLinks.length} · <span className="italic">{currentNode.name}</span>
          </div>
        </div>
        <div className="pointer-events-auto flex items-center gap-2">
          <div className="hidden sm:flex items-center text-[11px] text-white/70 bg-white/10 rounded px-2 py-1 gap-1">
            <Info className="h-3 w-3" /> Usa <kbd className="px-1 bg-white/20 rounded">← →</kbd> per navigare
          </div>
          {/* Auto-Walk Presentation toggle */}
          {!autoWalkOn ? (
            <div className="flex items-center gap-2 bg-white/10 border border-white/30 rounded pl-2 pr-1 py-0.5">
              <input type="range" min="2000" max="10000" step="500" value={autoWalkDwell}
                onChange={e => setAutoWalkDwell(Number(e.target.value))}
                className="w-20 accent-emerald-400" title={`Dwell ${(autoWalkDwell/1000).toFixed(1)}s`} data-testid="vtour-walk-dwell" />
              <span className="text-[10px] text-white/70 font-mono">{(autoWalkDwell/1000).toFixed(1)}s</span>
              <Button size="sm" onClick={startAutoWalk} className="bg-emerald-600 hover:bg-emerald-700 text-white h-7 border-0" data-testid="vtour-walk-play">
                <Play className="h-3 w-3 mr-1" /> Presentazione
              </Button>
            </div>
          ) : (
            <Button size="sm" onClick={stopAutoWalk} className="bg-red-600 hover:bg-red-700 text-white h-7 animate-pulse" data-testid="vtour-walk-stop">
              <Pause className="h-3 w-3 mr-1" /> Stop
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={onClose} className="bg-white/10 border-white/30 text-white hover:bg-white/20" data-testid="vtour-close">
            <X className="h-4 w-4 mr-1" /> Chiudi tour
          </Button>
        </div>
      </div>

      {/* Auto-Walk HUD (bottom center) */}
      {autoWalkOn && autoWalkLabel && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20 max-w-[80%]" data-testid="vtour-walk-hud">
          <div className="bg-black/70 backdrop-blur border border-white/20 rounded-lg px-4 py-2 text-white shadow-2xl">
            <div className="text-xs font-semibold flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
              {autoWalkLabel}
            </div>
            {autoWalkDetail && <div className="text-[10px] text-white/70 mt-0.5">{autoWalkDetail}</div>}
            <div className="mt-1 h-0.5 bg-white/20 rounded overflow-hidden">
              <div className="h-full bg-emerald-400" style={{ animation: `vtourProgress ${autoWalkDwell}ms linear` }} />
            </div>
          </div>
        </div>
      )}

      {/* Viewer container */}
      <div ref={containerRef} className="w-full h-full" />

      {/* Custom mini-map (bottom-left) — grid layout */}
      {!error && floorPlan.imageUrl && (
        <div
          className="absolute bottom-4 left-4 z-10 bg-white/95 backdrop-blur rounded-lg shadow-2xl border border-indigo-200 overflow-hidden pointer-events-auto"
          data-testid="vtour-minimap"
          style={{ width: 220 }}
        >
          <div className="px-2 py-1 text-[10px] font-semibold text-indigo-800 bg-indigo-50 border-b border-indigo-100 flex items-center justify-between">
            <span>Mappa Tour</span>
            <span className="text-indigo-500">{nodesWithLinks.length} scene</span>
          </div>
          <div className="relative">
            {/* Grid mini-map generated client-side */}
            <img src={floorPlan.imageUrl} alt="Mini-mappa" className="w-full h-auto block select-none" draggable={false} />
            {/* Clickable overlay dots (aligned with canvas coords) */}
            <div className="absolute inset-0">
              {nodesWithLinks.map((n, i) => {
                const [x, y] = floorPlan.coordsById[n.id] || [0, 0];
                const px = (x / (floorPlan.width || 1)) * 100;
                const py = (y / (floorPlan.height || 1)) * 100;
                const isActive = n.id === currentNode.id;
                return (
                  <button
                    key={n.id}
                    type="button"
                    onClick={() => jumpTo(n.id)}
                    title={n.name}
                    data-testid={`vtour-minimap-node-${i}`}
                    className={`absolute -translate-x-1/2 -translate-y-1/2 w-6 h-6 rounded-full border-2 flex items-center justify-center text-[10px] font-bold transition-all cursor-pointer ${
                      isActive
                        ? "bg-purple-600 text-white border-white shadow-lg scale-125 ring-2 ring-purple-300"
                        : "bg-white text-indigo-700 border-indigo-500 hover:bg-indigo-100 hover:scale-110"
                    }`}
                    style={{ left: `${px}%`, top: `${py}%` }}
                  >
                    {i + 1}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/80 text-white p-6 text-center">
          <div>
            <p className="font-semibold mb-2">Errore avvio tour</p>
            <p className="text-sm text-white/80 mb-4">{error}</p>
            <Button onClick={onClose} variant="outline" className="bg-white/10 border-white/30 text-white">Chiudi</Button>
          </div>
        </div>
      )}
    </div>
  );
}
